import { derivePreviousPeriod } from '@utils/derivePreviousPeriod';

export type HandedOverPeriod = {
  startDate: Date;
  endDate: Date;
  previousPeriodStartDate: Date;
  previousPeriodEndDate: Date;
};

/**
 * The period a screen was handed, with its comparison window guaranteed valid.
 *
 * A drill-down link carries the range the producer was looking at, but not every caller has a comparison
 * window to send: the funnel has none, and a hand-edited URL can drop or corrupt either end. Preserving
 * those values unchecked left the comparison dates undefined or reversed, and the payment card then fired
 * its previous-period request with no dates or backwards ones. The request failed, and every indicator
 * silently disappeared - and a missing indicator caused by a bad parameter looks exactly like "nothing to
 * compare", which is the failure mode this whole feature exists to remove.
 *
 * So a comparison window is honoured only when it is valid on its own terms, and derived otherwise, using
 * the arithmetic the custom-range modal applies: the window immediately before the period, of equal length.
 *
 * Returns undefined when the period itself is unusable, which is the caller's signal to fall back to its
 * own default rather than build a period out of half a URL.
 */
export const resolveHandedOverPeriod = (
  startDate?: Date,
  endDate?: Date,
  previousPeriodStartDate?: Date,
  previousPeriodEndDate?: Date,
): HandedOverPeriod | undefined => {
  if (!isUsableRange(startDate, endDate)) return undefined;

  const start = (startDate as Date).getTime();
  const end = (endDate as Date).getTime();
  const range = end - start;

  // Judged as a pair: one parseable date does not make a valid window, and a reversed comparison range
  // would be sent as-is and quietly kill every badge.
  //
  // A window that runs into the period is rejected for the same reason the derivation below leaves a
  // second: the two would share an instant and count a payment there twice. This is not only a
  // hand-edited-URL case - the custom-range modal shipped that exact pair until it was separated at the
  // source - so a link built by an older tab still arrives with one.
  if (
    isUsableRange(previousPeriodStartDate, previousPeriodEndDate) &&
    (previousPeriodEndDate as Date).getTime() < start
  )
    return {
      startDate: startDate as Date,
      endDate: endDate as Date,
      previousPeriodStartDate: previousPeriodStartDate as Date,
      previousPeriodEndDate: previousPeriodEndDate as Date,
    };

  return {
    startDate: startDate as Date,
    endDate: endDate as Date,
    ...derivePreviousPeriod(start, end),
  };
};

/**
 * A pair is usable only if it runs forwards and spans no more than the modal's limit.
 *
 * Reversed dates are the dangerous case: each one parses, so per-date checks pass, and the result is a
 * backwards period whose derived comparison window lands in the future.
 */
const isUsableRange = (start?: Date, end?: Date) => {
  if (!isUsable(start) || !isUsable(end)) return false;

  // Ordering is checked on the timestamps and the ceiling on whole days, because they answer different
  // questions. Measuring the ceiling in days is what makes an end-of-day range pass, but day indexes are
  // equal within a single day - so 20:00 to 10:00 on one day spans zero days and would slip through an
  // order check made in days alone, sending a backwards period.
  if ((start as Date).getTime() > (end as Date).getTime()) return false;

  return toDayIndex(end as Date) - toDayIndex(start as Date) <= MAX_RANGE_DAYS;
};

const isUsable = (date?: Date) => !!date && Number.isFinite(date.getTime());

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Six months in whole days, the same ceiling customPeriodRangeSchema enforces on the modal.
 *
 * Measured in days on purpose. The modal validates its 15778800000ms limit between two midnights and only
 * THEN pushes the end to 23:59:59, so the range it serialises is almost a day longer than the one it
 * validated. Comparing raw durations here rejected selections the UI had just accepted, and "Ver detalhes"
 * silently opened the default period instead. Whole days is the basis both sides agree on.
 */
const MAX_RANGE_DAYS = Math.floor(15778800000 / MS_PER_DAY);

/**
 * Index of the local calendar day, unaffected by how long that day actually lasted.
 *
 * Date.UTC of the LOCAL year/month/day, so a daylight-saving transition inside the range cannot shift the
 * count - the same reason resolveFunnelCoverage does it this way.
 */
const toDayIndex = (date: Date) =>
  Math.floor(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / MS_PER_DAY,
  );
